import React, { useEffect, useRef, useState } from "react";
import { useBets } from "../context/betsContext";
import { useAppContext } from "../context";

const EMPTY_BET = {
	fixtureIdentifier: null,
	groupIdentifier: null,
	playerBet: null,
	homeTeamScore: "",
	awayTeamScore: "",
};

const SAVE_DELAY_MS = 500;

const SAVE_STATUS_COPY = {
	pending: "Sin guardar",
	saving: "Guardando...",
	saved: "Guardado",
	error: "No se pudo guardar",
};

const SELECTED_WINNER_OPTION_STYLE = {
	background: "#dbeafe",
	borderColor: "#2563eb",
	boxShadow: "0 8px 18px rgba(37, 99, 235, 0.18)",
	color: "#1d4ed8",
};

const SELECTED_WINNER_LOGO_STYLE = {
	boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.18)",
};

const SELECTED_WINNER_DRAW_BADGE_STYLE = {
	background: "#bfdbfe",
	color: "#1d4ed8",
};

function SelfFixtureBetsScoreAndWinner({ infoPlayerBetsWithDateChanged }) {
	const { updateFixtureBet } = useBets();

	const { currentUniqueIdentifierSelected } = useAppContext();

	const [userBets, setUserBet] = useState([]);
	const [saveStatusByFixture, setSaveStatusByFixture] = useState({});
	const saveTimeoutsRef = useRef({});

	useEffect(() => {
		setUserBet(
			(infoPlayerBetsWithDateChanged || []).map((item) => ({
				...item,
				bet: {
					...EMPTY_BET,
					...item.bet,
				},
			}))
		);
	}, [infoPlayerBetsWithDateChanged]);

	useEffect(() => {
		const saveTimeouts = saveTimeoutsRef.current;

		return () => {
			Object.values(saveTimeouts).forEach((timeoutId) => {
				clearTimeout(timeoutId);
			});
		};
	}, []);

	const setFixtureSaveStatus = (fixtureIdentifier, status) => {
		setSaveStatusByFixture((previousState) => ({
			...previousState,
			[fixtureIdentifier]: status,
		}));
	};

	const queueSaveBet = (fixtureIdentifier, payload) => {
		if (saveTimeoutsRef.current[fixtureIdentifier]) {
			clearTimeout(saveTimeoutsRef.current[fixtureIdentifier]);
		}

		setFixtureSaveStatus(fixtureIdentifier, "pending");

		saveTimeoutsRef.current[fixtureIdentifier] = setTimeout(async () => {
			setFixtureSaveStatus(fixtureIdentifier, "saving");

			try {
				const response = await updateFixtureBet(payload);
				const hasError = response?.StatusCode >= 400;
				setFixtureSaveStatus(
					fixtureIdentifier,
					hasError ? "error" : "saved"
				);
			} catch (error) {
				setFixtureSaveStatus(fixtureIdentifier, "error");
			}
		}, SAVE_DELAY_MS);
	};

	const updateFixtureDraftBet = (fixtureIdentifier, nextBet) => {
		setUserBet((previousState) =>
			previousState.map((item) =>
				item.fixture.uniqueIdentifier === fixtureIdentifier
					? {
							...item,
							bet: nextBet,
					  }
					: item
			)
		);
	};

	const getSaveStatusLabel = (fixtureIdentifier) =>
		SAVE_STATUS_COPY[saveStatusByFixture[fixtureIdentifier]] || "";

	const getSaveStatusClassName = (fixtureIdentifier) => {
		const status = saveStatusByFixture[fixtureIdentifier];
		if (!status) return "fixture-card-status";

		return `fixture-card-status fixture-card-status-${status}`;
	};

	const getFixtureDate = (fixtureDate) => {
		const parsedDate = fixtureDate ? new Date(fixtureDate) : null;
		return Number.isNaN(parsedDate?.getTime()) ? null : parsedDate;
	};

	const getFixtureMeta = (fixture) => {
		const kickoffDate = getFixtureDate(fixture.rawDate || fixture.date);

		if (!kickoffDate) {
			return {
				kickoffText: fixture.date,
				deadlineText: "Disponible para pronosticar",
				isLocked: false,
			};
		}

		const dateFormatter = new Intl.DateTimeFormat("es-CO", {
			weekday: "short",
			day: "numeric",
			month: "short",
		});
		const timeFormatter = new Intl.DateTimeFormat("es-CO", {
			hour: "numeric",
			minute: "2-digit",
		});
		const kickoffDateText = dateFormatter.format(kickoffDate);
		const kickoffTimeText = timeFormatter.format(kickoffDate);

		return {
			kickoffText: `${kickoffDateText} - ${kickoffTimeText}`,
			deadlineText: `Cierra ${kickoffTimeText}`,
			isLocked: kickoffDate.getTime() <= Date.now(),
		};
	};

	const handleCheckRadiusOnchange = (
		event,
		groupIdentifier,
		fixtureIdentifier
	) => {
		const currentBet =
			userBets.find(
				(item) => item.fixture.uniqueIdentifier === fixtureIdentifier
			)?.bet || EMPTY_BET;
		const nextBet = {
			...currentBet,
			playerBet: Number(event.target.value),
		};

		updateFixtureDraftBet(fixtureIdentifier, nextBet);

		const completeInfoToUpdateBets = {
			fixtureIdentifier,
			groupIdentifier,
			winnerBet: nextBet.playerBet,
			homeTeamScore: null,
			awayTeamScore: null,
		};

		queueSaveBet(fixtureIdentifier, completeInfoToUpdateBets);
	};

	const handleInputOnchange = (
		event,
		groupIdentifier,
		fixtureIdentifier
	) => {
		const { name, value } = event.target;
		const nextScoreValue = value.slice(0, 2);
		const currentBet =
			userBets.find(
				(item) => item.fixture.uniqueIdentifier === fixtureIdentifier
			)?.bet || EMPTY_BET;
		const nextBet = {
			...currentBet,
			[name]: nextScoreValue,
		};

		updateFixtureDraftBet(fixtureIdentifier, nextBet);

		const completeInfoToUpdateBets = {
			fixtureIdentifier,
			groupIdentifier,
			winnerBet: null,
			homeTeamScore: Number(nextBet.homeTeamScore) || 0,
			awayTeamScore: Number(nextBet.awayTeamScore) || 0,
		};

		queueSaveBet(fixtureIdentifier, completeInfoToUpdateBets);
	};

	return (
		<React.Fragment>
			{userBets?.map((info) => {
				const fixtureIdentifier = info.fixture.uniqueIdentifier;
				const saveStatusLabel = getSaveStatusLabel(fixtureIdentifier);
				const saveStatusClassName =
					getSaveStatusClassName(fixtureIdentifier);
				const fixtureMeta = getFixtureMeta(info.fixture);
				const stageLabel =
					info.fixture.stage ||
					info.fixture.round ||
					info.fixture.roundName ||
					info.fixture.leagueRound ||
					null;

				if (
					info.group.fixtureBetTypeId === 2 &&
					info.group.uniqueIdentifier === currentUniqueIdentifierSelected
				) {
					return (
						<article
							key={`score-${fixtureIdentifier}`}
							className={`fixture-prediction-card ${
								fixtureMeta.isLocked ? "fixture-prediction-card-locked" : ""
							}`}
						>
							<div className='fixture-card-topbar'>
								<div className='fixture-card-kickoff'>
									<span className='fixture-card-date'>
										{fixtureMeta.kickoffText}
									</span>
									{stageLabel && (
										<span className='fixture-card-stage'>{stageLabel}</span>
									)}
								</div>
								<span className='fixture-card-lockstate'>
									{fixtureMeta.isLocked ? "Cerrado" : "Abierto"}
								</span>
							</div>

							<div className='fixture-card-body'>
								<div className='fixture-score-grid'>
									<label
										className='fixture-team-row'
										htmlFor={`input-fixtures-bets-home-${fixtureIdentifier}`}
									>
										<span className='fixture-team-main'>
											<img
												className='img-logo_team_fixture_bets'
												src={info.fixture.homeTeam.logoUrl}
												alt={`Escudo de ${info.fixture.homeTeam.name}`}
											/>
											<span className='fixture-team-name'>
												{info.fixture.homeTeam.name}
											</span>
										</span>
										<input
											className='form-control input-number-fixture-bets'
											type='number'
											min={0}
											max={99}
											name='homeTeamScore'
											id={`input-fixtures-bets-home-${fixtureIdentifier}`}
											inputMode='numeric'
											pattern='[0-9]*'
											disabled={fixtureMeta.isLocked}
											onChange={(event) =>
												handleInputOnchange(
													event,
													info.group.uniqueIdentifier,
													fixtureIdentifier
												)
											}
											value={info.bet.homeTeamScore ?? ""}
										/>
									</label>

									<label
										className='fixture-team-row'
										htmlFor={`input-fixtures-bets-away-${fixtureIdentifier}`}
									>
										<span className='fixture-team-main'>
											<img
												className='img-logo_team_fixture_bets'
												src={info.fixture.awayTeam.logoUrl}
												alt={`Escudo de ${info.fixture.awayTeam.name}`}
											/>
											<span className='fixture-team-name'>
												{info.fixture.awayTeam.name}
											</span>
										</span>
										<input
											className='form-control input-number-fixture-bets'
											type='number'
											min={0}
											max={99}
											name='awayTeamScore'
											id={`input-fixtures-bets-away-${fixtureIdentifier}`}
											inputMode='numeric'
											pattern='[0-9]*'
											disabled={fixtureMeta.isLocked}
											onChange={(event) =>
												handleInputOnchange(
													event,
													info.group.uniqueIdentifier,
													fixtureIdentifier
												)
											}
											value={info.bet.awayTeamScore ?? ""}
										/>
									</label>
								</div>
							</div>

							<div className='fixture-card-footer'>
								<span className={saveStatusClassName}>
									{saveStatusLabel || "Listo para editar"}
								</span>
								<span className='fixture-card-deadline'>
									{fixtureMeta.isLocked
										? "El partido ya comenzo"
										: fixtureMeta.deadlineText}
								</span>
							</div>
						</article>
					);
				}

				if (
					info.group.fixtureBetTypeId === 1 &&
					info.group.uniqueIdentifier === currentUniqueIdentifierSelected
				) {
					const isHomeWinnerSelected = info.bet.playerBet === 1;
					const isDrawSelected = info.bet.playerBet === 3;
					const isAwayWinnerSelected = info.bet.playerBet === 2;

					return (
						<article
							key={`winner-${fixtureIdentifier}`}
							className={`fixture-prediction-card ${
								fixtureMeta.isLocked ? "fixture-prediction-card-locked" : ""
							}`}
						>
							<div className='fixture-card-topbar'>
								<div className='fixture-card-kickoff'>
									<span className='fixture-card-date'>
										{fixtureMeta.kickoffText}
									</span>
									{stageLabel && (
										<span className='fixture-card-stage'>{stageLabel}</span>
									)}
								</div>
								<span className='fixture-card-lockstate'>
									{fixtureMeta.isLocked ? "Cerrado" : "Abierto"}
								</span>
							</div>

							<div className='fixture-card-body'>
								<div className='fixture-winner-segmented-control'>
									<label
										className={`fixture-winner-option ${
											isHomeWinnerSelected
												? "fixture-winner-option-selected"
												: ""
										}`}
										style={
											isHomeWinnerSelected
												? SELECTED_WINNER_OPTION_STYLE
												: undefined
										}
										htmlFor={`check-input-radio-bet-fixtures-1-${fixtureIdentifier}`}
									>
										<input
											checked={isHomeWinnerSelected}
											className='fixture-winner-option-input'
											type='radio'
											name={`playerBet-${fixtureIdentifier}`}
											id={`check-input-radio-bet-fixtures-1-${fixtureIdentifier}`}
											onChange={(event) =>
												handleCheckRadiusOnchange(
													event,
													info.group.uniqueIdentifier,
													fixtureIdentifier
												)
											}
											value={1}
											disabled={fixtureMeta.isLocked}
										/>
										<span className='fixture-winner-option-content'>
											<img
												className='fixture-winner-option-logo'
												src={info.fixture.homeTeam.logoUrl}
												alt={`Escudo de ${info.fixture.homeTeam.name}`}
												style={
													isHomeWinnerSelected
														? SELECTED_WINNER_LOGO_STYLE
														: undefined
												}
											/>
											<span>{info.fixture.homeTeam.name}</span>
										</span>
									</label>

									<label
										className={`fixture-winner-option ${
											isDrawSelected
												? "fixture-winner-option-selected"
												: ""
										}`}
										style={
											isDrawSelected
												? SELECTED_WINNER_OPTION_STYLE
												: undefined
										}
										htmlFor={`check-input-radio-bet-fixtures-3-${fixtureIdentifier}`}
									>
										<input
											checked={isDrawSelected}
											className='fixture-winner-option-input'
											type='radio'
											name={`playerBet-${fixtureIdentifier}`}
											id={`check-input-radio-bet-fixtures-3-${fixtureIdentifier}`}
											onChange={(event) =>
												handleCheckRadiusOnchange(
													event,
													info.group.uniqueIdentifier,
													fixtureIdentifier
												)
											}
											value={3}
											disabled={fixtureMeta.isLocked}
										/>
										<span className='fixture-winner-option-content fixture-winner-option-content-draw'>
											<span
												className='fixture-winner-option-draw-badge'
												style={
													isDrawSelected
														? SELECTED_WINNER_DRAW_BADGE_STYLE
														: undefined
												}
											>
												X
											</span>
											<span>Empate</span>
										</span>
									</label>

									<label
										className={`fixture-winner-option ${
											isAwayWinnerSelected
												? "fixture-winner-option-selected"
												: ""
										}`}
										style={
											isAwayWinnerSelected
												? SELECTED_WINNER_OPTION_STYLE
												: undefined
										}
										htmlFor={`check-input-radio-bet-fixtures-2-${fixtureIdentifier}`}
									>
										<input
											checked={isAwayWinnerSelected}
											className='fixture-winner-option-input'
											type='radio'
											name={`playerBet-${fixtureIdentifier}`}
											id={`check-input-radio-bet-fixtures-2-${fixtureIdentifier}`}
											onChange={(event) =>
												handleCheckRadiusOnchange(
													event,
													info.group.uniqueIdentifier,
													fixtureIdentifier
												)
											}
											value={2}
											disabled={fixtureMeta.isLocked}
										/>
										<span className='fixture-winner-option-content'>
											<img
												className='fixture-winner-option-logo'
												src={info.fixture.awayTeam.logoUrl}
												alt={`Escudo de ${info.fixture.awayTeam.name}`}
												style={
													isAwayWinnerSelected
														? SELECTED_WINNER_LOGO_STYLE
														: undefined
												}
											/>
											<span>{info.fixture.awayTeam.name}</span>
										</span>
									</label>
								</div>
							</div>

							<div className='fixture-card-footer'>
								<span className={saveStatusClassName}>
									{saveStatusLabel || "Listo para elegir"}
								</span>
								<span className='fixture-card-deadline'>
									{fixtureMeta.isLocked
										? "El partido ya comenzo"
										: fixtureMeta.deadlineText}
								</span>
							</div>
						</article>
					);
				}

				return null;
			})}
		</React.Fragment>
	);
}

export { SelfFixtureBetsScoreAndWinner };
